// Orele pe axa de timp și în zona goală din dreapta graficului — acolo unde lumânările
// încă nu s-au format.
//
// Lightweight Charts pune etichete de timp doar sub barele care există. Spațiul din dreapta
// (`rightOffset` și tot ce derulezi mai încolo) rămânea fără ore, deci nu vedeai cât timp e
// până la un nivel desenat acolo. Le desenăm noi, pe o pânză subțire peste axa lor, cu
// același format (UTC, ca biblioteca) — plus eticheta crucii când mouse-ul e în zona aceea.

import { el } from '../core/util.js';

const LUNI = ['янв.', 'февр.', 'мар.', 'апр.', 'мая', 'июн.', 'июл.', 'авг.', 'сент.', 'окт.', 'нояб.', 'дек.'];
/** Pașii posibili între etichete, în secunde; se alege primul care lasă ≥70 px între ele. */
const PASI = [60, 300, 900, 1800, 3600, 7200, 14400, 21600, 43200, 86400, 172800, 604800];
const MIN_PX = 70;

const doi = (n) => String(n).padStart(2, '0');

/** Formatul etichetei crucii, ca la bibliotecă: „13 окт. '26 14:10" (UTC). */
export function fmtTimpCruce(t) {
  const d = new Date(t * 1000);
  return `${d.getUTCDate()} ${LUNI[d.getUTCMonth()]} '${String(d.getUTCFullYear()).slice(2)} `
    + `${doi(d.getUTCHours())}:${doi(d.getUTCMinutes())}`;
}

function eticheta(t, pas) {
  const d = new Date(t * 1000);
  if (pas >= 86400 || (d.getUTCHours() === 0 && d.getUTCMinutes() === 0)) {
    return `${d.getUTCDate()} ${LUNI[d.getUTCMonth()]}`;
  }
  return `${doi(d.getUTCHours())}:${doi(d.getUTCMinutes())}`;
}

export class FutureAxis {
  /** @param {import('./chart.js').Chart} chart  @param {object} theme culorile graficului */
  constructor(chart, theme) {
    this.c = chart;
    this.theme = theme;
    this.hover = null;
    this.canvas = el('canvas', { class: 'chart-future-axis' });
    this.ctx = this.canvas.getContext('2d');
    chart.mainEl.append(this.canvas);
    // Crucea în zona viitoare: biblioteca nu are timp acolo (`param.time` lipsește),
    // dar are indicele logic — din el facem timpul.
    this.onMove = (p) => {
      const n = this.c.bars.length;
      const L = p?.logical;
      this.hover = (p?.point && p.time === undefined && L !== undefined && L !== null && L > n - 1)
        ? { x: p.point.x, t: this.c.timeForLogical(L) } : null;
      this.render();
    };
    chart.chart.subscribeCrosshairMove(this.onMove);
    this.ro = new ResizeObserver(() => this.render());
    this.ro.observe(chart.mainEl);
  }

  render() {
    const lw = this.c.chart;
    const ctx = this.ctx;
    if (!ctx || this.c.destroyed) return;
    const ts = lw.timeScale();
    let vizibila = true;
    try { vizibila = lw.options().timeScale.visible !== false; } catch { /* implicit */ }
    const bars = this.c.bars;
    const inaltime = vizibila ? ts.height() : 0;
    let axa = 60;
    try { axa = lw.priceScale('right').width() || 60; } catch { /* implicit */ }
    const W = Math.max(0, this.c.mainEl.clientWidth - axa);
    const dpr = window.devicePixelRatio || 1;
    if (this.canvas.width !== Math.round(W * dpr) || this.canvas.height !== Math.round(inaltime * dpr)) {
      this.canvas.width = Math.round(W * dpr);
      this.canvas.height = Math.round(inaltime * dpr);
      this.canvas.style.width = `${W}px`;
      this.canvas.style.height = `${inaltime}px`;
    }
    this.canvas.style.top = `${this.c.mainEl.clientHeight - inaltime}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, inaltime);
    if (!vizibila || !inaltime || bars.length < 2) return;

    const ultim = bars[bars.length - 1].time;
    const xUltim = ts.timeToCoordinate(ultim);
    if (xUltim === null) return;
    const pas = this.c.pasBare;
    let spatiu = 6;
    try { spatiu = ts.options().barSpacing || 6; } catch { /* implicit */ }
    const pxPeSec = spatiu / pas;
    const interval = PASI.find((s) => s * pxPeSec >= MIN_PX) || PASI[PASI.length - 1];

    ctx.font = "11px 'Inter', -apple-system, 'Segoe UI', sans-serif";
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = this.theme.text;
    // Prima etichetă: următorul multiplu „rotund" după ultima bară, destul de departe de
    // ultima etichetă a bibliotecii ca să nu se suprapună.
    for (let t = Math.ceil((ultim + pas) / interval) * interval; ; t += interval) {
      const x = xUltim + (t - ultim) * pxPeSec;
      if (x > W - 22) break;
      if (x < xUltim + 36) continue;
      ctx.fillText(eticheta(t, interval), x, inaltime / 2);
    }

    // Eticheta crucii în zona viitoare.
    if (this.hover && this.hover.t && this.hover.x <= W) {
      const text = fmtTimpCruce(this.hover.t);
      const lat = ctx.measureText(text).width + 14;
      const x0 = Math.max(0, Math.min(W - lat, this.hover.x - lat / 2));
      ctx.fillStyle = this.theme.border;
      ctx.fillRect(x0, 1, lat, inaltime - 2);
      ctx.fillStyle = this.theme.text;
      ctx.fillText(text, x0 + lat / 2, inaltime / 2);
    }
  }

  destroy() {
    try { this.c.chart.unsubscribeCrosshairMove(this.onMove); } catch { /* graficul a fost deja distrus */ }
    this.ro?.disconnect();
    this.canvas?.remove();
  }
}
